import type { ReactNode } from 'react';
import { StatRow } from '@/components/ui/StatRow';
import { StatusPopover } from '@/components/ui/StatusPopover';
import type { LastScanResult } from '@/lib/api';
import { formatRelative, formatSeconds } from '@/lib/format-duration';

interface LastScanPopoverProps {
  children: ReactNode;
  data: LastScanResult | null | undefined;
  isError: boolean;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export const LastScanPopover = ({
  children,
  data,
  isError,
  onOpenChange,
  open,
}: LastScanPopoverProps) => (
  <StatusPopover onOpenChange={onOpenChange} open={open} trigger={children}>
    <div className="flex items-start justify-between gap-2">
      <div className="font-medium text-foreground">Last scan results</div>
      {data ? (
        <div className="text-2xs text-subtle">
          {formatRelative(data.startedAt)} · {formatSeconds(data.durationMs)}
        </div>
      ) : null}
    </div>

    <div className="my-2 border-t border-border" />

    {isError ? (
      <div className="text-score-low">Failed to load scan results</div>
    ) : data === null ? (
      <div className="py-1 text-center text-subtle">No scans yet</div>
    ) : (
      <>
        <div className="flex flex-col gap-1">
          <StatRow
            label="Jobs found"
            value={`${data?.jobsNew ?? '—'} new · ${data?.jobsDeduped ?? '—'} deduped`}
          />
          <StatRow
            label="Scored"
            value={`${data?.scoredSucceeded ?? '—'} / ${data?.scoredAttempted ?? '—'}`}
          />
          <StatRow
            label="Tailored"
            value={
              <span>
                {`${data?.tailoredSucceeded ?? '—'} / ${data?.tailoredEligible ?? '—'} `}
                <span className="text-2xs text-subtle">≥ threshold</span>
              </span>
            }
          />
          <StatRow
            label="Errors"
            value={
              <span
                className={
                  (data?.errorsCount ?? 0) > 0
                    ? 'text-score-low'
                    : 'text-foreground-muted'
                }
              >
                {data?.errorsCount ?? '—'}
              </span>
            }
          />
        </div>

        {data?.perSource ? (
          <>
            <div className="my-2 border-t border-border" />
            <div className="mb-1 text-2xs text-subtle">PER SOURCE</div>
            <div className="flex flex-col gap-1">
              {Object.entries(data.perSource).map(([source, info]) => (
                <div
                  className="flex items-center justify-between gap-3"
                  key={source}
                >
                  <span className="text-foreground-muted">
                    {source.toUpperCase()}
                  </span>
                  {info.errors.length > 0 ? (
                    <span
                      className="max-w-40 truncate text-score-low"
                      title={info.errors[0]}
                    >
                      {info.errors[0]}
                    </span>
                  ) : (
                    <span className="text-foreground">{info.new} new</span>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : null}
      </>
    )}
  </StatusPopover>
);
